import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, BellRing, CheckCircle2, Circle, Crown, ExternalLink, Lock, MessageSquarePlus, PenLine, Users } from 'lucide-react';
import { Button } from '../Button';
import { RichTextContent } from '../RichTextContent';
import { RichTextEditor } from '../RichTextEditor';
import { initSocket } from '../../services/socketService';
import type { Analytics } from '../../services/caseRegistratieApi';
import {
  behandelSuggestie,
  bewaarTekst,
  claimCoordinator,
  dienSuggestieIn,
  draagOver,
  getTeam,
  getTeamJaar,
  releaseTeam,
  verlaatTeam,
  vraagAudit,
  zetRedactierol,
  zetSectieSlot,
  zetVerificatie,
  type VasteOnderdelen,
  type Redactierol,
  type TekstRegel,
} from '../../services/teamJaarverslagApi';

/**
 * Geconsolideerd jaarverslag bij meerdere VTP's/BMW'ers (migratie 174).
 *  - Mijn team: wie er bij deze opdrachtgever registreert en wie coördinator is.
 *    Het team regelt de rol zelf; de werkgever ziet of beheert dit niet.
 *  - Jaarverslag: iedereen verifieert zijn eigen data, schrijft mee aan de
 *    teksten en ziet het totaal; alleen de coördinator geeft vrij.
 *  - Ook wie alleen registreert, werkt hier (BMW sinds migratie 185, VTP sinds
 *    190); daarbij komen contactgegevens, tevredenheid en de terugblik op vorig
 *    jaar (H2, H5.4, BMW H8 / VTP H9), en bij VTP de activiteiten (H6).
 *  - Redactie (migratie 189): redacteuren schrijven, bijdragers dienen
 *    suggesties in; wie typt, houdt het hoofdstuk vast en collega's zien dat live.
 */
const foutmelding = (e: any) => toast.error(e?.response?.data?.message || 'Er ging iets mis');
const datumLabel = (s: string) => new Date(s).toLocaleDateString('nl-NL');

export const teamJaarKey = (employerId: string, jaar: number) => ['team-jaarverslag', employerId, jaar];

/** Mag ik de teksten zelf aanpassen (redacteur of coördinator)? Via context, zodat elk blok met hoofdstukken het meekrijgt. */
const RedactieContext = createContext({ magSchrijven: true });

/** Slot verlengen zolang er getypt wordt; wie langer stil is, laat het slot verlopen (server: LOCK_SECONDEN). */
const SLOT_HARTSLAG_MS = 20_000;
const STIL_NA_MS = 2 * 60_000;

const ROL_LABEL: Record<Redactierol, string> = { redacteur: 'Redacteur', bijdrager: 'Bijdrager' };

/** Suggesties bij één hoofdstuk: een redacteur neemt ze over of wijst ze af; een bijdrager dient ze in. */
const Suggesties: React.FC<{ employerId: string; jaar: number; regel: TekstRegel; magSchrijven: boolean; lokaalBewerkt: boolean; onBijgewerkt: () => void }> = ({
  employerId,
  jaar,
  regel,
  magSchrijven,
  lokaalBewerkt,
  onBijgewerkt,
}) => {
  const [tekst, setTekst] = useState('');
  const [sleutel, setSleutel] = useState(0);
  const indienen = useMutation({
    mutationFn: () => dienSuggestieIn(employerId, jaar, regel.code, tekst),
    onSuccess: () => {
      toast.success('Suggestie ingediend bij de redactie');
      setTekst('');
      setSleutel((k) => k + 1);
      onBijgewerkt();
    },
    onError: foutmelding,
  });
  const behandel = useMutation({
    mutationFn: (v: { id: string; actie: 'overnemen' | 'afwijzen' }) => behandelSuggestie(employerId, jaar, v.id, v.actie),
    onSuccess: (_, v) => (toast.success(v.actie === 'overnemen' ? 'Suggestie aan de tekst toegevoegd' : 'Suggestie afgewezen'), onBijgewerkt()),
    onError: foutmelding,
  });
  const zichtbaar = regel.suggesties.filter((s) => s.status === 'open' || s.is_mijn);

  return (
    <div className="space-y-2">
      {zichtbaar.map((s) => (
        <div key={s.id} className="rounded-md border border-indigo-100 bg-indigo-50/50 p-3 space-y-2">
          <p className="text-xs text-indigo-900">
            Suggestie van {s.is_mijn ? 'jou' : s.naam} · {datumLabel(s.ingediend_op)}
            {s.status !== 'open' && <span className="ml-2 font-medium">{s.status === 'verwerkt' ? 'verwerkt' : 'niet overgenomen'}</span>}
          </p>
          <RichTextContent html={s.tekst} className="rounded-md bg-white p-2 text-sm" />
          {magSchrijven && s.status === 'open' && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={lokaalBewerkt || !!regel.bezig_door}
                loading={behandel.isPending && behandel.variables?.id === s.id && behandel.variables.actie === 'overnemen'}
                onClick={() => behandel.mutate({ id: s.id, actie: 'overnemen' })}
              >
                Achteraan de tekst overnemen
              </Button>
              <Button
                size="sm"
                variant="outline"
                loading={behandel.isPending && behandel.variables?.id === s.id && behandel.variables.actie === 'afwijzen'}
                onClick={() => behandel.mutate({ id: s.id, actie: 'afwijzen' })}
              >
                Afwijzen
              </Button>
              {lokaalBewerkt && <span className="text-xs text-gray-500">Sla eerst je eigen wijziging op.</span>}
            </div>
          )}
        </div>
      ))}
      {!magSchrijven && (
        <div className="space-y-2">
          <p className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
            <MessageSquarePlus className="w-4 h-4" aria-hidden /> Tekst voorstellen voor dit hoofdstuk
          </p>
          <RichTextEditor key={sleutel} simple zonderLinks minHeight={80} content="" placeholder="Je voorstel; een redacteur verwerkt het in de tekst." onChange={setTekst} />
          <Button size="sm" variant="outline" disabled={!tekst.replace(/<[^>]*>/g, '').trim()} loading={indienen.isPending} onClick={() => indienen.mutate()}>
            Suggestie indienen
          </Button>
        </div>
      )}
    </div>
  );
};

/** Eén hoofdstuk. Bij een versieconflict blijft de eigen tekst staan en verschijnt die van de collega ernaast. */
const HoofdstukEditor: React.FC<{ employerId: string; jaar: number; regel: TekstRegel; alleenLezen: boolean; onOpgeslagen: () => void }> = ({
  employerId,
  jaar,
  regel,
  alleenLezen,
  onOpgeslagen,
}) => {
  const { magSchrijven } = useContext(RedactieContext);
  const [tekst, setTekst] = useState(regel.tekst);
  const [basis, setBasis] = useState(regel.versie);
  // Alleen echte bewerkingen tellen: de editor normaliseert HTML, dus vergelijken met de server-tekst zegt niets.
  const [bewerkt, setBewerkt] = useState(false);
  // Tiptap leest `content` alleen bij het aanmaken; een nieuwe sleutel laadt een andere tekst in.
  const [editorSleutel, setEditorSleutel] = useState(0);
  const [conflict, setConflict] = useState<{ tekst: string; versie: number } | null>(null);
  const laatsteToets = useRef(0);
  const laad = (t: string, v: number) => {
    setTekst(t);
    setBasis(v);
    setBewerkt(false);
    setEditorSleutel((k) => k + 1);
  };

  // Nieuwe versie van de server (bijv. een collega) overnemen zolang er niets lokaal gewijzigd is.
  useEffect(() => {
    if (!bewerkt && regel.versie !== basis) laad(regel.tekst, regel.versie);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regel.tekst, regel.versie]);

  // Zolang er lokale wijzigingen zijn, houd je het hoofdstuk vast; opslaan, overnemen of weggaan geeft het weer vrij.
  useEffect(() => {
    if (!bewerkt || alleenLezen || !magSchrijven) return;
    const neem = () => {
      if (Date.now() - laatsteToets.current > STIL_NA_MS) return;
      zetSectieSlot(employerId, jaar, regel.code, true).catch((e) => {
        if (e?.response?.status === 423) toast.error(e.response.data?.message, { id: `slot-${regel.code}` });
      });
    };
    neem();
    const t = window.setInterval(neem, SLOT_HARTSLAG_MS);
    return () => {
      window.clearInterval(t);
      zetSectieSlot(employerId, jaar, regel.code, false).catch(() => undefined);
    };
  }, [bewerkt, alleenLezen, magSchrijven, employerId, jaar, regel.code]);

  // Een collega typt hier: alleen lezen, tenzij je zelf al wijzigingen had (opslaan geeft dan een melding).
  const bezet = !alleenLezen && !!regel.bezig_door && !bewerkt;

  const opslaan = useMutation({
    mutationFn: () => bewaarTekst(employerId, jaar, regel.code, tekst, basis),
    onSuccess: (r) => {
      setBasis(r.versie);
      setBewerkt(false);
      setConflict(null);
      toast.success(`${regel.titel} opgeslagen`);
      onOpgeslagen();
    },
    onError: (e: any) => {
      const data = e?.response?.status === 409 ? e.response.data?.data : null;
      if (data && typeof data.versie === 'number') setConflict({ tekst: data.tekst, versie: data.versie });
      foutmelding(e);
    },
  });

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-gray-900">
          {regel.nummer} {regel.titel}
        </h3>
        {regel.bijgewerkt_door && (
          <span className="text-xs text-gray-500">
            Laatst door {regel.bijgewerkt_door}
            {regel.bijgewerkt_op ? ` · ${datumLabel(regel.bijgewerkt_op)}` : ''}
          </span>
        )}
      </div>
      {bezet && (
        <p className="flex items-center gap-1.5 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <PenLine className="w-4 h-4" aria-hidden /> Collega {regel.bezig_door} typt momenteel in deze sectie…
        </p>
      )}
      {alleenLezen || bezet || !magSchrijven ? (
        regel.tekst ? (
          <RichTextContent html={regel.tekst} className="rounded-md border border-gray-100 bg-gray-50 p-3" />
        ) : (
          <p className="text-sm italic text-gray-500">Niet ingevuld.</p>
        )
      ) : (
        <RichTextEditor
          key={editorSleutel}
          simple
          zonderLinks
          minHeight={140}
          content={tekst}
          placeholder="Zonder herleidbare details: geen namen, functies of data van individuele meldingen."
          onChange={(html) => {
            laatsteToets.current = Date.now();
            setTekst(html);
            setBewerkt(true);
          }}
        />
      )}
      {conflict && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
          <p className="flex items-center gap-1.5 text-sm font-medium text-amber-900">
            <AlertTriangle className="w-4 h-4" aria-hidden /> Een collega heeft dit hoofdstuk intussen aangepast
          </p>
          <p className="text-xs text-amber-900">Versie van je collega:</p>
          <RichTextContent html={conflict.tekst || '<p><em>(leeg)</em></p>'} className="rounded-md border border-amber-200 bg-white p-3" />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                laad(conflict.tekst, conflict.versie);
                setConflict(null);
              }}
            >
              Versie van collega overnemen
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                // Bouw voort op de nieuwste versie; de eigen tekst blijft staan zodat je zelf kunt samenvoegen.
                setBasis(conflict.versie);
                setConflict(null);
              }}
            >
              Mijn tekst houden en samenvoegen
            </Button>
          </div>
        </div>
      )}
      {!alleenLezen && magSchrijven && !bezet && (
        <Button size="sm" disabled={!bewerkt || !!conflict} loading={opslaan.isPending} onClick={() => opslaan.mutate()}>
          Opslaan
        </Button>
      )}
      {!alleenLezen && (
        <Suggesties employerId={employerId} jaar={jaar} regel={regel} magSchrijven={magSchrijven} lokaalBewerkt={bewerkt} onBijgewerkt={onOpgeslagen} />
      )}
    </div>
  );
};

/**
 * De vaste onderdelen zoals ze bij de release in het verslag komen: H2 contactgegevens, H5.4 tevredenheid,
 * bij VTP H6 (activiteiten en beroepsborging) en per aanbeveling van vorig jaar een evaluatie (BMW H8, VTP H9).
 */
const OnderdelenBlok: React.FC<{
  employerId: string;
  jaar: number;
  discipline: 'VTP' | 'BMW';
  onderdelen: VasteOnderdelen;
  alleenLezen: boolean;
  onOpgeslagen: () => void;
}> = ({ employerId, jaar, discipline, onderdelen, alleenLezen, onOpgeslagen }) => {
  const t = onderdelen.tevredenheid;
  const tb = onderdelen.terugblik;
  const act = onderdelen.activiteiten;
  const borging = onderdelen.beroepsborging;
  const terugblikNr = discipline === 'VTP' ? 9 : 8;
  return (
    <div className="space-y-5 border-t border-gray-100 pt-4">
      <div className="space-y-1">
        <h3 className="text-sm font-medium text-gray-900">2 Contactgegevens</h3>
        <p className="text-xs text-gray-500">De actieve teamleden met het e-mailadres en telefoonnummer uit hun account, zoals ze bij de release in het verslag komen.</p>
        {onderdelen.contactpersonen.length === 0 ? (
          <p className="text-sm text-gray-500">Geen actieve teamleden.</p>
        ) : (
          <ul className="space-y-0.5 text-sm text-gray-800">
            {onderdelen.contactpersonen.map((c) => (
              <li key={`${c.naam}|${c.email ?? ''}`}>
                {c.naam} · {c.email ?? '—'} · {c.telefoon ?? 'geen telefoonnummer'}
                {c.register_geverifieerd && <span className="ml-2 rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-800">Register geverifieerd</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="space-y-1">
        <h3 className="text-sm font-medium text-gray-900">5.4 Ervaringen van melders</h3>
        <p className="text-sm text-gray-700">
          {t && t.gemiddelde != null
            ? `${t.gemiddelde.toFixed(1).replace('.', ',')} / 5, gemiddeld over ${t.aantal} anonieme evaluaties in ${jaar}.`
            : `Minder dan ${t?.min_groep ?? 10} evaluaties door medewerkers in ${jaar}; de score komt niet in het verslag.`}
        </p>
      </div>
      {act && (
        <div className="space-y-1">
          <h3 className="text-sm font-medium text-gray-900">6.1 Voorlichting & informatie — tellingen</h3>
          <p className="text-xs text-gray-500">Uit de zichtbaarheidsrondes en de geaccordeerde ad-hoc uren van het team bij deze opdrachtgever in {jaar}. Je eigen toelichting schrijf je hierboven onder 6.1.</p>
          <ul className="space-y-0.5 text-sm text-gray-800">
            <li>Zichtbaarheidsrondes op locatie: {act.zichtbaarheidsrondes}</li>
            <li>Ad-hoc gesprekken op de werkvloer (bevestigd): {act.werkvloergesprekken}</li>
            <li>Interne workshops of teamsessies: {act.workshops}</li>
            <li>Overleg met leidinggevenden of preventiemedewerker: {act.overleggen}</li>
          </ul>
        </div>
      )}
      {borging && (
        <div className="space-y-1">
          <h3 className="text-sm font-medium text-gray-900">6.2 Educatie en intervisie</h3>
          <p className="text-xs text-gray-500">Alleen wat BeLagom kan aantonen: BeLagom-intervisie en Academy-scholing met afgeronde laatste les in {jaar}, zoals op het Kwaliteitspaspoort.</p>
          {borging.length === 0 ? (
            <p className="text-sm text-gray-500">Geen actieve teamleden.</p>
          ) : (
            <ul className="space-y-0.5 text-sm text-gray-800">
              {borging.map((b) => (
                <li key={b.naam}>
                  {b.naam} · register {b.register_geverifieerd ? 'geverifieerd' : 'niet geverifieerd'} · {b.intervisie_sessies} intervisiesessies ·{' '}
                  {b.pe_punten.toLocaleString('nl-NL')} PE-punten
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-900">
          {terugblikNr} Terugblik op de aanbevelingen uit {tb?.jaar ?? jaar - 1}
        </h3>
        {!tb ? (
          <p className="text-sm text-gray-500">
            Er is geen vrijgegeven teamverslag over {jaar - 1}. Het verslag vermeldt dat in hoofdstuk {terugblikNr}.
          </p>
        ) : (
          tb.items.map((it, i) => (
            <div key={it.evaluatie.code} className="rounded-md border border-gray-200 p-3 space-y-2">
              <p className="text-xs text-gray-500">Aanbeveling {i + 1} uit {tb.jaar}</p>
              <RichTextContent html={it.aanbeveling} className="rounded-md bg-gray-50 p-2 text-sm" />
              <HoofdstukEditor employerId={employerId} jaar={jaar} regel={it.evaluatie} alleenLezen={alleenLezen} onOpgeslagen={onOpgeslagen} />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export const TeamJaarverslagPaneel: React.FC<{
  employerId: string;
  jaar: number;
  renderPreview: (p: Analytics) => React.ReactNode;
}> = ({ employerId, jaar, renderPreview }) => {
  const qc = useQueryClient();
  const [doel, setDoel] = useState('');
  const teamQuery = useQuery({ queryKey: ['team', employerId], queryFn: () => getTeam(employerId), retry: false });
  // Elke 30 s verversen: teksten en verificaties van collega's verschijnen zonder herladen.
  const jaarQuery = useQuery({
    queryKey: teamJaarKey(employerId, jaar),
    queryFn: () => getTeamJaar(employerId, jaar),
    retry: false,
    enabled: teamQuery.isSuccess,
    refetchInterval: 30_000,
  });
  const team = teamQuery.data;
  const st = jaarQuery.data;
  const verversen = () => {
    qc.invalidateQueries({ queryKey: ['team', employerId] });
    qc.invalidateQueries({ queryKey: ['team-jaarverslag', employerId] });
    qc.invalidateQueries({ queryKey: ['case-audit', employerId] });
  };

  // Realtime: een rolwissel of release door een collega komt als notificatie binnen; dan direct de rechten verversen.
  useEffect(() => {
    const socket = initSocket();
    if (!socket) return;
    const ververs = () => {
      qc.invalidateQueries({ queryKey: ['team', employerId] });
      qc.invalidateQueries({ queryKey: ['team-jaarverslag', employerId] });
    };
    // Een collega nam of gaf een hoofdstuk vrij, sloeg tekst op of behandelde een suggestie.
    const sectie = (p: { employer_id?: string; jaar?: number }) => {
      if (p?.employer_id === employerId && typeof p.jaar === 'number') qc.invalidateQueries({ queryKey: teamJaarKey(employerId, p.jaar) });
    };
    socket.on('new_notification', ververs);
    socket.on('team_jaarverslag_sectie', sectie);
    return () => {
      socket.off('new_notification', ververs);
      socket.off('team_jaarverslag_sectie', sectie);
    };
  }, [qc, employerId]);

  const overdragen = useMutation({
    mutationFn: () => draagOver(employerId, doel),
    onSuccess: () => {
      toast.success('Coördinatorrol overgedragen');
      setDoel('');
      verversen();
    },
    onError: foutmelding,
  });
  const claimen = useMutation({ mutationFn: () => claimCoordinator(employerId), onSuccess: () => (toast.success('Je bent nu coördinator'), verversen()), onError: foutmelding });
  const verlaten = useMutation({ mutationFn: () => verlaatTeam(employerId), onSuccess: () => (toast.success('Je hebt het team verlaten'), verversen()), onError: foutmelding });
  const verificatie = useMutation({
    mutationFn: (aan: boolean) => zetVerificatie(employerId, jaar, aan),
    onSuccess: (_, aan) => (toast.success(aan ? 'Je data is geverifieerd' : 'Verificatie ingetrokken'), verversen()),
    onError: foutmelding,
  });
  const rol = useMutation({
    mutationFn: (v: { professionalId: string; rol: Redactierol }) => zetRedactierol(employerId, v.professionalId, v.rol),
    onSuccess: (_, v) => (toast.success(`Rol gewijzigd naar ${ROL_LABEL[v.rol].toLowerCase()}`), verversen()),
    onError: foutmelding,
  });
  const audit = useMutation({
    mutationFn: () => vraagAudit(employerId, jaar),
    onSuccess: (r) => (toast.success(`${r.aantal} ${r.aantal === 1 ? 'collega' : 'collega’s'} gevraagd te verifiëren`), verversen()),
    onError: foutmelding,
  });
  const release = useMutation({
    mutationFn: () => releaseTeam(employerId, jaar),
    onSuccess: () => (toast.success(`Geconsolideerd jaarverslag ${jaar} vrijgegeven aan de opdrachtgever`), verversen()),
    onError: foutmelding,
  });

  if (teamQuery.isLoading) return <p className="text-sm text-gray-500">Laden…</p>;
  if (teamQuery.isError || !team) {
    return (
      <p className="text-sm text-gray-600 max-w-2xl">
        {(teamQuery.error as any)?.response?.status === 403
          ? 'Je zit nog niet in het team bij deze opdrachtgever. Dat gebeurt vanzelf bij je eerste registratie.'
          : (teamQuery.error as any)?.response?.data?.message || 'Het team kon niet worden geladen.'}
      </p>
    );
  }

  const collegas = team.leden.filter((l) => !l.is_ik);
  const ik = st?.deelnemers.find((x) => x.is_ik);
  const vrijgegeven = st?.status === 'released';
  const kanReleasen = !!st && !vrijgegeven && st.ik_ben_coordinator && st.deelnemers.length > 0 && st.nog_te_verifieren === 0 && st.wachtend_op_bevestiging === 0;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-6 items-start">
      <div className="space-y-6">
        <section className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#0B1F3A]" aria-hidden />
            <h2 className="font-semibold text-gray-900">Mijn team bij deze opdrachtgever</h2>
          </div>
          <p className="text-xs text-gray-500">
            Het team kiest zelf wie het jaarverslag coördineert. De opdrachtgever ziet dit niet en kan het niet wijzigen.
            Redacteuren schrijven de teksten; bijdragers lezen mee en dienen suggesties in. De coördinator wijst dat toe.
          </p>
          <ul className="divide-y divide-gray-100">
            {team.leden.map((l) => (
              <li key={l.professional_id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-gray-900">
                  {l.naam}
                  {l.is_ik && <span className="text-gray-500"> (jij)</span>}
                </span>
                {l.is_coordinator ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 px-2 py-0.5 text-xs font-medium">
                    <Crown className="w-3 h-3" aria-hidden /> Coördinator
                  </span>
                ) : team.ik_ben_coordinator ? (
                  <select
                    className="rounded-md border-gray-300 py-0.5 text-xs"
                    value={l.redactierol}
                    disabled={rol.isPending}
                    onChange={(e) => rol.mutate({ professionalId: l.professional_id, rol: e.target.value as Redactierol })}
                    aria-label={`Rol van ${l.naam}`}
                  >
                    <option value="redacteur">{ROL_LABEL.redacteur}</option>
                    <option value="bijdrager">{ROL_LABEL.bijdrager}</option>
                  </select>
                ) : (
                  <span className="rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-xs">{ROL_LABEL[l.redactierol]}</span>
                )}
              </li>
            ))}
          </ul>
          {team.ik_ben_coordinator && collegas.length > 0 && (
            <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3">
              <select className="flex-1 min-w-[12rem] rounded-md border-gray-300 text-sm" value={doel} onChange={(e) => setDoel(e.target.value)} aria-label="Collega">
                <option value="">Kies collega…</option>
                {collegas.map((c) => (
                  <option key={c.professional_id} value={c.professional_id}>{c.naam}</option>
                ))}
              </select>
              <Button
                size="sm"
                variant="outline"
                disabled={!doel}
                loading={overdragen.isPending}
                onClick={() => {
                  const naam = collegas.find((c) => c.professional_id === doel)?.naam;
                  if (window.confirm(`De coördinatorrol overdragen aan ${naam}? Je verliest daarmee de knop om het jaarverslag vrij te geven.`)) overdragen.mutate();
                }}
              >
                <Crown className="w-4 h-4 mr-1" aria-hidden /> Rol overdragen aan collega
              </Button>
            </div>
          )}
          {team.coordinator_vacant && (
            <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-900 flex flex-wrap items-center justify-between gap-2">
              <span>Het team heeft geen actieve coördinator.</span>
              <Button size="sm" loading={claimen.isPending} onClick={() => claimen.mutate()}>Coördinatorrol opnemen</Button>
            </div>
          )}
          <button
            type="button"
            className="text-xs text-gray-500 underline hover:text-gray-800"
            onClick={() => {
              if (window.confirm('Het team bij deze opdrachtgever verlaten? Je registraties blijven meetellen; met een nieuwe registratie word je weer lid.')) verlaten.mutate();
            }}
          >
            Team verlaten
          </button>
        </section>

        <section className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold text-gray-900">Geconsolideerd jaarverslag {jaar}</h2>
            {vrijgegeven ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs font-medium">
                <Lock className="w-3 h-3" aria-hidden /> Vrijgegeven op {st?.vrijgegeven_op ? datumLabel(st.vrijgegeven_op) : ''}
              </span>
            ) : (
              <span className="rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-xs font-medium">Concept</span>
            )}
          </div>
          {jaarQuery.isLoading && <p className="text-sm text-gray-500">Laden…</p>}
          {st && (
            <>
              {vrijgegeven && st.verificatie_url && (
                <a href={st.verificatie_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#0B1F3A] underline">
                  Verificatiepagina <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                </a>
              )}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-1">Deelnemers ({st.deelnemers.length})</h3>
                {st.deelnemers.length === 0 ? (
                  <p className="text-sm text-gray-500">Nog geen registraties in {jaar}.</p>
                ) : (
                  <ul className="space-y-1">
                    {st.deelnemers.map((d) => (
                      <li key={d.professional_id} className="flex items-center gap-2 text-sm">
                        {d.geverifieerd || vrijgegeven ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" aria-label="Geverifieerd" />
                        ) : (
                          <Circle className="w-4 h-4 text-gray-300" aria-label="Nog niet geverifieerd" />
                        )}
                        <span className="text-gray-900">{d.naam}{d.is_ik && <span className="text-gray-500"> (jij)</span>}</span>
                        {!d.actief && <span className="text-xs text-gray-500">vertrokken — telt mee zonder verificatie</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {!vrijgegeven && ik && (
                <div className="rounded-md border border-gray-200 p-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-gray-700">
                    {ik.geverifieerd
                      ? 'Je hebt je eigen registraties geverifieerd. Een nieuwe wijziging trekt dat automatisch in.'
                      : 'Controleer je eigen registraties op het tabblad "Controleren" en bevestig daarna.'}
                  </p>
                  <Button size="sm" variant={ik.geverifieerd ? 'outline' : 'primary'} loading={verificatie.isPending} onClick={() => verificatie.mutate(!ik.geverifieerd)}>
                    {ik.geverifieerd ? 'Verificatie intrekken' : '✓ Mijn data is geverifieerd'}
                  </Button>
                </div>
              )}

              {st.wachtend_op_bevestiging > 0 && !vrijgegeven && (
                <p className="flex gap-1.5 text-xs text-amber-800">
                  <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden />
                  {st.wachtend_op_bevestiging} ad-hoc {st.wachtend_op_bevestiging === 1 ? 'gesprek wacht' : 'gesprekken wachten'} nog op bevestiging door de medewerker.
                </p>
              )}

              <RedactieContext.Provider value={{ magSchrijven: st.ik_mag_schrijven }}>
              {!vrijgegeven && !st.ik_mag_schrijven && (
                <p className="rounded-md bg-gray-50 p-3 text-xs text-gray-700">
                  Je bent bijdrager: je leest de teksten mee en stelt per hoofdstuk een tekst voor. Een redacteur verwerkt die.
                </p>
              )}
              <div className="space-y-5 border-t border-gray-100 pt-4">
                {st.teksten.map((regel) => (
                  <HoofdstukEditor
                    key={regel.code}
                    employerId={employerId}
                    jaar={jaar}
                    regel={regel}
                    alleenLezen={vrijgegeven}
                    onOpgeslagen={() => qc.invalidateQueries({ queryKey: teamJaarKey(employerId, jaar) })}
                  />
                ))}
              </div>

              {st.onderdelen && (
                <OnderdelenBlok
                  employerId={employerId}
                  jaar={jaar}
                  discipline={st.discipline}
                  onderdelen={st.onderdelen}
                  alleenLezen={vrijgegeven}
                  onOpgeslagen={() => qc.invalidateQueries({ queryKey: teamJaarKey(employerId, jaar) })}
                />
              )}
              </RedactieContext.Provider>

              {!vrijgegeven && st.ik_ben_coordinator && (
                <div className="border-t border-gray-100 pt-4 space-y-2">
                  {st.nog_te_verifieren > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs text-gray-600">
                        Nog {st.nog_te_verifieren} {st.nog_te_verifieren === 1 ? 'collega moet' : 'collega’s moeten'} verifiëren.
                        {st.audit_verzoek_op && ` Laatst gevraagd op ${datumLabel(st.audit_verzoek_op)}.`}
                      </p>
                      <Button size="sm" variant="outline" loading={audit.isPending} onClick={() => audit.mutate()}>
                        <BellRing className="w-4 h-4 mr-1" aria-hidden /> Team vragen te verifiëren
                      </Button>
                    </div>
                  )}
                  <Button
                    className="w-full"
                    disabled={!kanReleasen}
                    loading={release.isPending}
                    onClick={() => {
                      if (window.confirm(`Het geconsolideerde jaarverslag ${jaar} definitief vrijgeven aan de opdrachtgever? Cijfers en teksten liggen daarna vast.`)) release.mutate();
                    }}
                  >
                    <Lock className="w-4 h-4 mr-2" aria-hidden /> Integraal jaarverslag valideren & releasen
                  </Button>
                </div>
              )}
            </>
          )}
          {jaarQuery.isError && <p className="text-sm text-red-700">{(jaarQuery.error as any)?.response?.data?.message || 'Het jaarverslag kon niet worden geladen.'}</p>}
        </section>
      </div>

      <aside className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900">{vrijgegeven ? 'Vrijgegeven cijfers' : 'Preview voor de werkgever'}</h3>
          <p className="text-xs text-gray-500">Over alle professionals samen berekend; groepen onder de grens zijn samengevoegd.</p>
        </div>
        {st?.preview ? renderPreview(st.preview) : <p className="text-sm text-gray-500">Nog geen cijfers.</p>}
      </aside>
    </div>
  );
};

export default TeamJaarverslagPaneel;
